import { create } from 'zustand';
import { MAX_LIVES } from '@/config/constants';
import type { MathOperation } from '@/types/models';

export type Screen = 'menu' | 'game' | 'tutorial' | 'gameover' | 'leaderboard' | 'settings';

interface GameState {
  screen: Screen;
  level: number;
  score: number;
  combo: number;
  lives: number;
  currentOp: MathOperation | null;
  startedAt: number | null;
  lastResultMs: number | null;

  setScreen: (s: Screen) => void;
  startGame: () => void;
  startTutorial: () => void;
  setOperation: (op: MathOperation) => void;
  registerHit: (points: number) => void;
  registerMiss: () => void;
  loseLife: () => void;
  nextLevel: () => void;
  endGame: () => void;
  reset: () => void;
}

export const useGameStore = create<GameState>((set, get) => ({
  screen: 'menu',
  level: 0,
  score: 0,
  combo: 0,
  lives: MAX_LIVES,
  currentOp: null,
  startedAt: null,
  lastResultMs: null,

  setScreen: (screen) => set({ screen }),

  startGame: () =>
    set({
      screen: 'game',
      level: 0,
      score: 0,
      combo: 0,
      lives: MAX_LIVES,
      currentOp: null,
      startedAt: Date.now(),
      lastResultMs: null
    }),

  startTutorial: () => set({ screen: 'tutorial', currentOp: null }),

  setOperation: (op) => set({ currentOp: op }),

  registerHit: (points) =>
    set((s) => ({
      score: s.score + points,
      combo: s.combo + 1,
      lastResultMs: Date.now()
    })),

  registerMiss: () =>
    set((s) => ({
      combo: 0,
      lives: Math.max(0, s.lives - 1),
      lastResultMs: Date.now()
    })),

  loseLife: () => set((s) => ({ lives: Math.max(0, s.lives - 1) })),

  nextLevel: () => set((s) => ({ level: s.level + 1 })),

  endGame: () => {
    const s = get();
    set({
      screen: 'gameover',
      lastResultMs: Date.now(),
      startedAt: s.startedAt
    });
  },

  reset: () =>
    set({
      screen: 'menu',
      level: 0,
      score: 0,
      combo: 0,
      lives: MAX_LIVES,
      currentOp: null,
      startedAt: null,
      lastResultMs: null
    })
}));

export function durationSeconds(state: GameState): number {
  if (!state.startedAt) return 0;
  const end = state.lastResultMs ?? Date.now();
  return Math.max(0, Math.floor((end - state.startedAt) / 1000));
}
